import type { ToastInterface } from "vue-toastification";
import ClipboardWarning from "@/components/ClipboardWarning.vue";
import { calculateTimeout, copyToClipboard } from "./utilities";
import { logError, logWarn } from "./logError";

/**
 * Only the three methods this helper uses. Narrowed from vue-toastification's own
 * ToastInterface rather than hand-rolled, so the real toast object satisfies it without a
 * cast and a signature change in the library surfaces here as a type error.
 */
export type ClipboardToastHandler = Pick<ToastInterface, "success" | "info" | "dismiss">;

export interface CopyLinkToClipboardOptions {
	/**
	 * Resolves to the URL being copied. The caller MUST have already started this
	 * promise — see the user-activation note on `copyLinkToClipboard`.
	 */
	urlPromise: Promise<string>;
	/** Noun used in the toast copy, e.g. "recording request", "project", "video". */
	noun: string;
	toast: ClipboardToastHandler;
	/**
	 * Overrides the success toast text. Several suites assert these strings verbatim
	 * (e.g. "The contact's link to this recording request has been copied to the
	 * clipboard."), so callers with established wording pass their own.
	 */
	successMessage?: string;
}

/**
 * Copies a link to the clipboard in a way that survives Safari's user-activation rules,
 * falling back to an informational toast that shows the link for manual copying.
 *
 * Safari grants a page a short-lived transient user activation on tap and consumes it on
 * the first `await`. So `await fetchUrl(); await clipboard.writeText(url)` always fails on
 * iOS Safari — by the time the write runs the activation is spent. Passing the *promise*
 * into `ClipboardItem` inverts that: the clipboard call starts inside the activation window
 * and Safari keeps the activation alive while the inner promise resolves. Originally fixed
 * for artifact share links in LSP-15622; generalised here for LSP-18938.
 *
 * IMPORTANT: `urlPromise` must be constructed by the caller *before* calling this function
 * and without any intervening `await`, otherwise the activation is already gone.
 *
 * @throws the original URL-fetch error (unwrapped) when `urlPromise` rejects, or an Error when it
 *         resolves to a blank/non-string URL. There is no usable link in either case, so no
 *         manual-copy fallback is shown and reporting it is the caller's decision.
 */
export async function copyLinkToClipboard({ urlPromise, noun, toast, successMessage }: CopyLinkToClipboardOptions): Promise<void> {
	const onCopied = () => {
		const message = successMessage ?? `The ${noun} link has been copied to the clipboard.`;
		toast.success(message, { timeout: calculateTimeout(message) });
	};

	// Validate once, before either branch, so a blank URL cannot reach the clipboard on one path
	// while being rejected on the other. Copying "" or "null" would show the success toast while
	// leaving the clipboard useless — the same silent-failure class this ticket exists to remove —
	// so a URL we cannot use is treated like a URL we never got: rethrown, with no manual-copy
	// fallback, because there is nothing worth showing the user.
	const validatedUrlPromise = urlPromise
		.then((url) => {
			if (typeof url !== "string" || url.trim() === "") {
				throw new Error(`copyLinkToClipboard: refusing to copy an unusable ${noun} link (received ${JSON.stringify(url)}).`);
			}

			return url;
		})
		.catch((urlError) => {
			// Logged HERE, at the single point both branches consume, rather than in the
			// ClipboardItem catch below — the legacy path rethrows without passing through that
			// catch, so logging there left browsers without async-clipboard support with no
			// diagnostic at all. Note this fires for BOTH a genuine fetch failure and this
			// function's own blank-URL refusal, so a log reader should not assume a network fault.
			logError(`[CopyLink] Could not obtain a usable ${noun} link; no manual-copy fallback will be offered.`, urlError, { noun });
			throw urlError;
		});

	if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
		// When a promise handed to ClipboardItem rejects, navigator.clipboard.write rejects with
		// that same error, so "the fetch failed" and "the clipboard refused" are indistinguishable
		// at the catch site — yet they need opposite handling. Record which one happened in a
		// closure-local flag rather than wrapping the error in a marker class: only our own catch
		// on our own promise can set it, so nothing the clipboard rejects with can be mistaken for
		// a fetch failure. Scoped inside this branch on purpose — built unconditionally, this
		// derived promise would have no consumer on the legacy path below and would surface as an
		// unhandledRejection in our Datadog error telemetry.
		let urlFetchFailure: { cause: unknown } | null = null;
		const trackedUrlPromise = validatedUrlPromise.catch((urlFetchError) => {
			urlFetchFailure = { cause: urlFetchError };
			throw urlFetchError;
		});

		try {
			const blobPromise = trackedUrlPromise.then((url) => new Blob([url], { type: "text/plain" }));
			// Belt-and-suspenders: if clipboard.write rejects before it iterates the
			// ClipboardItem's inner promises AND the URL fetch also rejects, blobPromise
			// inherits trackedUrlPromise's rejection with no consumer. This fire-and-forget
			// catch attaches a handler so V8 considers it handled.
			blobPromise.catch(() => undefined);
			await navigator.clipboard.write([new ClipboardItem({ "text/plain": blobPromise })]);
			// A conforming clipboard.write settles only after consuming the ClipboardItem's inner
			// promises, but an implementation that acknowledges the write early would otherwise let
			// us claim success while the only URL source is still pending — and possibly about to
			// fail. Awaiting the (normally already-settled) promise makes the success claim honest.
			await trackedUrlPromise;
			onCopied();
			return;
		} catch (asyncClipboardError) {
			const fetchFailure = urlFetchFailure as { cause: unknown } | null;
			if (fetchFailure) {
				// Never got the URL — there is nothing to offer for manual copying. Already logged
				// at validatedUrlPromise's construction, so this only classifies and rethrows.
				throw fetchFailure.cause;
			}

			logWarn(`[CopyLink] Async clipboard write failed for the ${noun} link; falling back to the legacy copy.`, {
				noun,
				reason: String(asyncClipboardError)
			});
		}
	} else {
		// Diagnostic for the silent-failure case: without this, a browser missing either
		// half of the async clipboard API looks identical to a permission denial in logs.
		logWarn(`[CopyLink] Async clipboard API unavailable for the ${noun} link; using the legacy copy.`, {
			noun,
			hasClipboardItem: typeof ClipboardItem !== "undefined",
			hasClipboardWrite: !!navigator.clipboard?.write
		});
	}

	// urlPromise has already started; await its value here. A rejection propagates to the caller
	// unwrapped — and is logged by validatedUrlPromise's own catch above, which both branches share,
	// so this path cannot lose the diagnostic.
	const url = await validatedUrlPromise;

	if (await copyToClipboard(url)) {
		onCopied();
		return;
	}

	logWarn(`[CopyLink] Clipboard copy failed for the ${noun} link. Showing the manual copy fallback.`, { noun });

	// Informational, not an error: the user did nothing wrong, and the fallback hands them
	// the link so the action is still completable.
	const fallbackToast = toast.info(
		{
			component: ClipboardWarning,
			props: {
				url,
				artifactType: noun,
				onClose: () => {
					toast.dismiss(fallbackToast);
				}
			}
		},
		{
			timeout: 10000,
			draggable: false,
			// vue-toastification defaults closeOnClick to true, which would dismiss this toast the
			// moment the user taps the URL field or the Copy button — the two things it exists to be
			// tapped. That unmounts ClipboardWarning, whose in-flight guard then discards the copy
			// outcome, so the link disappears with no success toast and no guidance. Found by Bugbot
			// on PR #4351 as an interaction between the fallback toast and that unmount guard.
			closeOnClick: false,
			toastClassName: "toast-align-top"
		}
	);
}
